import { ApiError, type Account, type BrowserAuthManager } from "@groundspark/browser-auth";
import { CanvassApi } from "./api";
import { ProjectDrafts } from "./drafts";
import {
  type Branch,
  type IssuedSession,
  type Project,
  type ProjectSummary,
  type Register,
  type Selection,
  type Session,
} from "./domain";

/** All grants and retained voter data belong to one signed-in organisation. */
export class CanvassingAccess {
  public sessions: readonly Session[] = [];
  public projects: readonly ProjectSummary[] = [];
  public branches: readonly Branch[] = [];
  public registers: readonly Register[] = [];
  public selection: Selection | null = null;
  public initialized = false;
  public error: unknown = null;
  public readonly issued = new Map<string, IssuedSession>();
  private readonly drafts = new Map<string, ProjectDrafts>();
  private readonly listeners = new Set<() => void>();
  private readonly revokedListeners = new Set<(project: string) => void>();
  private sessionsGeneration = 0;
  private metadataGeneration = 0;
  private version: number;
  private role: Account["role"];
  private active = true;
  private refreshing: Promise<void> | null = null;
  private sessionRefresh: Promise<void> | null = null;
  private poll: ReturnType<typeof setInterval> | null = null;
  private expiry: ReturnType<typeof setTimeout> | null = null;
  private lastAccountRefresh = 0;

  public constructor(
    public readonly api: CanvassApi,
    private readonly auth: Pick<BrowserAuthManager, "account" | "refreshAccount">,
    private readonly onAuthenticationFailure: (failure: unknown) => void,
    private readonly now = Date.now,
  ) {
    const account = auth.account;
    if (!account) throw new Error("Sign in before loading canvassing access.");
    this.version = account.accessVersion;
    this.role = account.role;
  }
  public get account(): Account {
    if (!this.auth.account) throw new Error("Sign in again.");
    return this.auth.account;
  }
  public get manager(): boolean {
    return this.active && this.role !== "member";
  }
  public get currentSession(): Session | null {
    const selection = this.selection;
    return selection?.kind === "session"
      ? (this.sessions.find(({ id }) => id === selection.id) ?? null)
      : null;
  }
  public get currentProject(): Project | null {
    const selection = this.selection;
    return selection?.kind === "project"
      ? (this.projects.find(({ id }) => id === selection.id) ?? null)
      : (this.currentSession?.project ?? null);
  }
  public get accessibleProjects(): readonly Project[] {
    return [
      ...new Map(
        [...this.projects, ...this.sessions.map(({ project }) => project)].map((project) => [
          project.id,
          project,
        ]),
      ).values(),
    ];
  }
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  public onRevoke(listener: (project: string) => void): () => void {
    this.revokedListeners.add(listener);
    return () => this.revokedListeners.delete(listener);
  }
  public async start(): Promise<void> {
    await this.refresh();
    if (!this.active) return;
    this.poll = setInterval(() => {
      this.expire();
      if (this.now() - this.lastAccountRefresh >= 15_000) void this.refresh();
      else void this.refreshSessions();
    }, 1000);
  }
  public refresh(): Promise<void> {
    if (!this.active) return Promise.resolve();
    if (this.refreshing !== null) return this.refreshing;
    this.lastAccountRefresh = this.now();
    const operation = this.loadAccess();
    this.refreshing = operation;
    void operation.finally(() => {
      if (this.refreshing === operation) this.refreshing = null;
    });
    return operation;
  }
  private async loadAccess(): Promise<void> {
    try {
      const account = await this.auth.refreshAccount();
      if (!this.active) return;
      if (this.version !== account.accessVersion || this.role !== account.role) {
        this.version = account.accessVersion;
        this.role = account.role;
        this.sessionsGeneration += 1;
        this.metadataGeneration += 1;
        this.sessionRefresh = null;
        this.sessions = [];
        this.projects = [];
        this.branches = [];
        this.registers = [];
        this.issued.clear();
        this.reconcile();
      }
      this.error = null;
      await Promise.all([this.refreshSessions(true), this.loadMetadata()]);
    } catch (error) {
      if (!this.active) return;
      this.error = error;
      this.onAuthenticationFailure(error);
    } finally {
      if (this.active) {
        this.initialized = true;
        this.notify();
      }
    }
  }
  private async loadMetadata(): Promise<void> {
    const generation = ++this.metadataGeneration;
    const [projects, branches, registers] = await Promise.all([
      this.manager ? this.api.projects() : Promise.resolve([]),
      this.manager ? this.api.branches() : Promise.resolve([]),
      this.manager ? this.api.registers() : Promise.resolve([]),
    ]);
    if (!this.active || generation !== this.metadataGeneration) return;
    this.projects = projects;
    this.branches = branches;
    this.registers = registers;
    this.reconcile();
  }
  public refreshSessions(force = false): Promise<void> {
    if (!this.active) return Promise.resolve();
    if (!force && this.sessionRefresh !== null) return this.sessionRefresh;
    const generation = ++this.sessionsGeneration;
    const operation = this.api
      .sessions()
      .then((sessions) => {
        if (!this.active || generation !== this.sessionsGeneration) return;
        const changed = JSON.stringify(this.sessions) !== JSON.stringify(sessions);
        this.sessions = sessions;
        this.reconcile(changed);
      })
      .catch((error: unknown) => {
        if (!this.active || generation !== this.sessionsGeneration) return;
        this.error = error;
        this.onAuthenticationFailure(error);
        this.notify();
      })
      .finally(() => {
        if (this.sessionRefresh === operation) this.sessionRefresh = null;
      });
    this.sessionRefresh = operation;
    return operation;
  }
  public select(selection: Selection | null): void {
    if (!this.active) return;
    this.expire();
    if (selection !== null && !this.canSelect(selection)) return;
    this.selection = selection;
    this.notify();
  }
  private canSelect(selection: Selection): boolean {
    return selection.kind === "session"
      ? this.sessions.some(({ id }) => id === selection.id)
      : this.manager && this.projects.some(({ id }) => id === selection.id);
  }
  public hasProject(id: string): boolean {
    return this.active && this.accessibleProjects.some((project) => project.id === id);
  }
  public canWriteProject(id: string): boolean {
    return (
      this.active &&
      this.sessions.some(
        (session) => session.project.id === id && Date.parse(session.expires_at) > this.now(),
      )
    );
  }
  public projectDrafts(project: string): ProjectDrafts {
    if (!this.hasProject(project)) throw new Error("Project access ended.");
    let drafts = this.drafts.get(project);
    if (!drafts) {
      drafts = new ProjectDrafts(async (id, edit, signal) => {
        if (!this.canWriteProject(project))
          throw new Error("Join an active session to retry these edits.");
        try {
          return await this.api.saveElector(project, id, edit, signal);
        } catch (error) {
          this.handleProjectError(project, error);
          throw error;
        }
      });
      this.drafts.set(project, drafts);
    }
    return drafts;
  }
  public get pendingProjects(): readonly { project: Project; drafts: ProjectDrafts }[] {
    return this.accessibleProjects.flatMap((project) => {
      const drafts = this.drafts.get(project.id);
      return drafts && drafts.pending.length > 0 ? [{ project, drafts }] : [];
    });
  }
  public handleProjectError(project: string, error: unknown): void {
    this.onAuthenticationFailure(error);
    if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
      // Hide and purge immediately; an authoritative reload can restore surviving grants.
      this.projects = this.projects.filter(({ id }) => id !== project);
      this.sessions = this.sessions.filter((session) => session.project.id !== project);
      this.reconcile();
      void this.refresh();
    }
  }
  private expire(): void {
    if (this.sessions.some((session) => Date.parse(session.expires_at) <= this.now()))
      this.reconcile();
  }
  private reconcile(changed = true): void {
    const before = this.sessions.length;
    this.sessions = this.sessions.filter(
      (session) => Date.parse(session.expires_at) > this.now(),
    );
    if (before !== this.sessions.length) changed = true;
    const ids = new Set(this.accessibleProjects.map(({ id }) => id));
    for (const [id, drafts] of this.drafts)
      if (!ids.has(id)) {
        drafts.dispose();
        this.drafts.delete(id);
        for (const listener of this.revokedListeners) listener(id);
      }
    for (const id of this.issued.keys())
      if (!this.sessions.some((session) => session.id === id)) this.issued.delete(id);
    if (this.selection !== null && !this.canSelect(this.selection)) {
      this.selection = null;
      changed = true;
    }
    if (this.selection === null && this.sessions.length === 1) {
      this.selection = { kind: "session", id: this.sessions[0]!.id };
      changed = true;
    }
    if (this.expiry !== null) clearTimeout(this.expiry);
    if (this.sessions.length > 0) {
      const expires = Math.min(
        ...this.sessions.map((session) => Date.parse(session.expires_at)),
      );
      this.expiry = setTimeout(() => this.expire(), Math.max(0, expires - this.now()));
    }
    if (changed) this.notify();
  }
  public async startSession(project: string): Promise<void> {
    const issued = await this.api.startSession(project);
    if (!this.active) return;
    this.issued.set(issued.session.id, issued);
    await this.refreshSessions(true);
    this.select({ kind: "session", id: issued.session.id });
  }
  public async joinSession(code: string): Promise<void> {
    const session = await this.api.joinSession(code);
    if (!this.active) return;
    await this.refreshSessions(true);
    this.select({ kind: "session", id: session.id });
  }
  public async regenerate(id: string): Promise<void> {
    const issued = await this.api.regenerateSession(id);
    if (!this.active) return;
    this.issued.set(id, issued);
    await this.refreshSessions(true);
    this.notify();
  }
  public async extend(id: string): Promise<void> {
    await this.api.extendSession(id);
    await this.refreshSessions(true);
  }
  public async end(id: string): Promise<void> {
    await this.api.endSession(id);
    if (!this.active) return;
    this.sessions = this.sessions.filter((session) => session.id !== id);
    this.reconcile();
    await this.refreshSessions(true);
  }
  public async kick(session: string, user: string): Promise<void> {
    await this.api.kickParticipant(session, user);
    await this.refreshSessions(true);
  }
  public dispose(): void {
    this.active = false;
    this.sessionsGeneration += 1;
    this.metadataGeneration += 1;
    if (this.poll !== null) clearInterval(this.poll);
    if (this.expiry !== null) clearTimeout(this.expiry);
    for (const drafts of this.drafts.values()) drafts.dispose();
    this.drafts.clear();
    this.sessions = this.projects = this.branches = this.registers = [];
    this.selection = null;
    this.issued.clear();
    this.listeners.clear();
    this.revokedListeners.clear();
  }
  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}
